import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Search, Code, Settings, BookOpen, CreditCard, ExternalLink } from 'lucide-react';
import { Platform } from '../../contexts/PlatformContext';
import { PLATFORM_URL } from '../../config/platformUrls';

interface QuickLinksOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  platform: Platform;
  onPlatformSwitch: (platform: Platform) => void;
}

interface CommandItem {
  id: string;
  name: string;
  href?: string;
  icon: React.ComponentType<any>;
  action?: () => void;
  external?: boolean;
}

export function QuickLinksOverlay({ isOpen, onClose }: QuickLinksOverlayProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Custom commands
  const commands: CommandItem[] = [
    {
      id: 'developer-platform',
      name: 'Go to Developer Platform',
      href: PLATFORM_URL,
      icon: Code,
      external: true,
    },
    {
      id: 'subscribe',
      name: 'Subscribe to Pro',
      href: '/account?tab=subscription',
      icon: CreditCard,
    },
    {
      id: 'account',
      name: 'Account Settings',
      href: '/account',
      icon: Settings,
    },
    {
      id: 'docs',
      name: 'Documentation',
      href: 'https://docs.jua.ai',
      icon: BookOpen,
      external: true,
    },
  ];

  // Combine commands
  const allItems = [...commands.map((cmd) => ({ ...cmd, type: 'command' as const }))];

  const filteredItems = allItems.filter(
    (item) =>
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.href && item.href.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
      setSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && filteredItems.length > 0) {
      const selectedElement = listRef.current.children[selectedIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex, filteredItems.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, filteredItems.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredItems[selectedIndex]) {
          handleItemSelect(filteredItems[selectedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  };

  const handleItemSelect = (item: any) => {
    if (item.action) {
      // Handle custom commands with actions
      item.action();
    } else if (item.href) {
      // Handle navigation
      if (item.external) {
        window.open(item.href, '_blank');
      } else {
        navigate(item.href);
      }
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-start justify-center p-4 text-center sm:p-0">
        <div
          className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
          onClick={onClose}
        />

        <div className="relative w-full max-w-lg transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 shadow-xl transition-all sm:my-8 sm:w-full">
          <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Quick Links</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="relative mb-4">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400 dark:text-gray-500" />
              </div>
              <input
                ref={searchInputRef}
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#E34290] focus:border-[#E34290]"
                placeholder="Search pages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filteredItems.length > 0 ? (
                <ul ref={listRef} className="space-y-1">
                  {filteredItems.map((item, index) => {
                    const Icon = item.icon;
                    return (
                      <li key={item.id}>
                        <button
                          className={`w-full text-left px-3 py-2 rounded-lg flex items-center space-x-3 transition-colors ${
                            index === selectedIndex
                              ? 'bg-[#E34290]/10 dark:bg-[#E34290]/20 text-[#E34290] dark:text-[#E34290]'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                          onClick={() => handleItemSelect(item)}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1">{item.name}</span>
                          {item.external && <ExternalLink className="h-3 w-3 ml-1 text-gray-400" />}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  No items found matching "{searchQuery}"
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center justify-between">
              <span>Use ↑↓ to navigate, ⏎ to select, ESC to close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
